import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Gift, Headphones, Music } from 'lucide-react';
import { CtaButton } from './CtaButton';
export function BonusSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const sosUseCases = ['En el pico de una crisis de ansiedad', 'Cuando sientes el pecho apretado sin saber por qué', 'Antes de una situación que te genera estrés anticipatorio', 'A las 3AM cuando la mente empieza su loop nocturno'];
  const sessions = ['Inducción al sueño profundo — apaga la mente en menos de 10 minutos', 'Corte de rumiación nocturna — disuelve el loop de pensamientos al acostarte', 'Reprogramación de la respuesta al estrés — entrena la calma automática', 'Regulación del sistema nervioso autónomo — reduce la hipervigilancia crónica', 'Restauración de energía profunda — sueño reparador de ciclo completo', 'Anclaje de seguridad interior — instala la calma como estado por defecto', 'Integración y reset total — consolida todos los cambios en una sesión de cierre'];
  return <section ref={ref} className="w-full px-6 py-10" data-id="element-15">
      <div className="max-w-4xl mx-auto space-y-10" data-id="element-16">
        {/* Bonus 1 */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.6
      }} className="bg-amber-gold/10 border-2 border-amber-gold/30 rounded-xl p-8 md:p-10" data-id="element-17">
          <div className="flex items-center gap-3 mb-6" data-id="element-18">
            <div className="bg-amber-gold text-dark-navy rounded-full px-4 py-2 font-bold flex items-center gap-2" data-id="element-19">
              <Gift className="w-5 h-5" data-id="element-20" />
              <span data-id="element-21">BONO 1</span>
            </div>
            <span className="text-warm-cream/60" data-id="element-22">— INCLUIDO HOY</span>
          </div>

          <div className="flex items-center gap-4 mb-4" data-id="element-23">
            <Headphones className="w-8 h-8 text-amber-gold" strokeWidth={1.5} data-id="element-24" />
            <h3 className="font-playfair text-3xl md:text-4xl font-bold" data-id="element-25">
              Audio SOS: Calma Instantánea
            </h3>
          </div>

          <p className="text-xl md:text-2xl font-playfair font-semibold text-amber-gold mb-6" data-id="element-26">
            El Botón de Pánico Digital
          </p>

          <img src="https://cdn.magicpatterns.com/uploads/pmjE4Xt8LhDCQVx3zzgZbf/Bono_1.jpg" alt="Audio SOS: Calma Instantánea — Emergency Anxiety Relief" className="w-full max-w-md mx-auto rounded-lg mb-8" data-id="element-27" />

          <p className="text-lg md:text-xl mb-6" data-id="element-28">
            Hay momentos en los que la ansiedad no avisa. Aparece de golpe, en
            medio del día, y necesitas una respuesta inmediata — no un protocolo
            de 15 pasos.
          </p>

          <p className="text-lg md:text-xl mb-6" data-id="element-29">
            Para eso existe este audio de emergencia.
          </p>

          <p className="text-lg md:text-xl mb-8" data-id="element-30">
            En menos de 3 minutos, te guía a través de una secuencia de
            respiración y regulación somática diseñada para detener la espiral
            ansiosa en seco. Corta la rumiación aguda. Baja el cortisol.
            Devuelve el control.
          </p>

          <p className="text-lg md:text-xl font-semibold mb-4" data-id="element-31">
            Es literalmente un botón de pánico que puedes activar desde el
            bolsillo, en cualquier lugar, en cualquier momento.
          </p>

          <div className="mt-8" data-id="element-32">
            <p className="font-playfair text-xl font-semibold mb-4" data-id="element-33">
              ¿Cuándo usarlo?
            </p>
            <ul className="space-y-3" data-id="element-34">
              {sosUseCases.map((useCase, index) => <li key={index} className="flex items-start gap-3" data-id="element-35">
                  <span className="text-amber-gold mt-1" data-id="element-36">•</span>
                  <span className="text-warm-cream/90" data-id="element-37">{useCase}</span>
                </li>)}
            </ul>
          </div>

          <p className="text-lg md:text-xl font-medium text-amber-gold mt-8" data-id="element-38">
            No necesitas estar en calma para usarlo. Está diseñado para el peor
            momento.
          </p>
        </motion.div>

        {/* Bonus 2 */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        delay: 0.2,
        duration: 0.6
      }} className="bg-amber-gold/10 border-2 border-amber-gold/30 rounded-xl p-8 md:p-10" data-id="element-39">
          <div className="flex items-center gap-3 mb-6" data-id="element-40">
            <div className="bg-amber-gold text-dark-navy rounded-full px-4 py-2 font-bold flex items-center gap-2" data-id="element-41">
              <Gift className="w-5 h-5" data-id="element-42" />
              <span data-id="element-43">BONO 2</span>
            </div>
            <span className="text-warm-cream/60" data-id="element-44">— INCLUIDO HOY</span>
          </div>

          <div className="flex items-center gap-4 mb-4" data-id="element-45">
            <Music className="w-8 h-8 text-amber-gold" strokeWidth={1.5} data-id="element-46" />
            <h3 className="font-playfair text-3xl md:text-4xl font-bold" data-id="element-47">
              Pack de Audios Premium: 7 Sesiones de Neuro-Hipnosis
            </h3>
          </div>

          <p className="text-xl md:text-2xl font-playfair font-semibold text-amber-gold mb-6" data-id="element-48">
            Automatiza la Calma. Entrena el Sistema Nervioso. Sin Esfuerzo
            Consciente.
          </p>

          <img src="https://cdn.magicpatterns.com/uploads/a3Ev9LpXf1mxwUCFcAyCs1/Bono_2.jpg" alt="Pack de Audios Premium — 7 Sesiones de Neuro-Hipnosis" className="w-full max-w-md mx-auto rounded-lg mb-8" data-id="element-49" />

          <p className="text-lg md:text-xl mb-6" data-id="element-50">
            Aquí está la diferencia entre manejar la ansiedad y transformarla de
            raíz.
          </p>

          <p className="text-lg md:text-xl mb-8" data-id="element-51">
            El Manual NEURO RESET Somático te da las herramientas activas. Las 7
            sesiones de Neuro-Hipnosis hacen algo diferente: reprograman tu
            sistema nervioso mientras duermes o descansas, automatizando la
            respuesta de calma a nivel subconsciente.
          </p>

          <p className="text-lg md:text-xl font-medium mb-8" data-id="element-52">
            No tienes que esforzarte. No tienes que "hacer" nada. Solo dale al
            play.
          </p>

          <div className="space-y-4 mb-8" data-id="element-53">
            {sessions.map((session, index) => <div key={index} className="flex items-start gap-4 bg-dark-navy/30 rounded-lg p-4" data-id="element-54">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-gold/20 flex items-center justify-center" data-id="element-55">
                  <span className="text-amber-gold font-bold" data-id="element-56">{index + 1}</span>
                </div>
                <p className="text-warm-cream/90 pt-1" data-id="element-57">{session}</p>
              </div>)}
          </div>

          <p className="text-lg md:text-xl font-medium text-amber-gold" data-id="element-58">
            El resultado acumulado de 14 días de uso: Tu sistema nervioso
            aprende — a nivel biológico — que la calma es su estado natural. No
            necesitas fuerza de voluntad para relajarte. Tu cuerpo lo hace solo.
          </p>
        </motion.div>

        <CtaButton text="Dame el botiquín de primeros auxilios para mi mente" subtitle="Kit completo + Audio SOS + 7 sesiones de Neuro-Hipnosis" data-id="element-59" />
      </div>
    </section>;
}